'use client'
import Title from '../Title/index.js'
import { cn } from '../../helpers/utils.js'
import Picture from '../../components/picture.js'
import Button from '../../components/button.js'
import SwiperBox from '../SwiperBox/index.js'
import { withLayout } from '../../shared/Styles.js'
import React, { useState, useRef, useCallback, useImperativeHandle, useEffect, type RefObject } from 'react'
import { useExposure } from '../../hooks/useExposure.js'
import { trackUrlRef } from '../../shared/trackUrlRef.js'
import type { Img } from '../../types/props.js'
import throttle from '../../shared/throttle.js'
import { Heading } from '../../components/heading.js'

const componentType = 'image'
const componentName = 'scene_banner'

/** Hook：监听窗口宽度 */
type ItemType = {
  /** 标题文本 */
  title: string
  /** 图片*/
  img: Img
  mobileImg?: Img
  /** 内容节点 */
  subTitle: string
  link?: string
  imgLink?: string
}

export type AccordionCardsType = Omit<React.HTMLAttributes<HTMLDivElement>, 'className'> & {
  className?: string
  data: {
    key?: string
    title?: string
    products: Array<ItemType>
    /** 按钮形状 */
    shape?: 'round' | 'square'
    /** 卡片形状 */
    itemShape?: 'round' | 'square'
    /** 主按钮配置 */
    primaryButton?: string
    theme?: 'light' | 'dark'
  }
  /**
   * 自动播放配置
   */
  autoplay?: {
    /**
     * 是否开启自动播放
     */
    enabled: boolean
    /**
     * 是否循环播放
     */
    loop?: boolean
    /**
     * 自动播放间隔时间，单位毫秒
     */
    interval?: number
  }
  /** 按钮事件*/
  event?: {
    primaryButton?: (_v: any, _index: number) => void
  }
}

const AccordionCardsPcItem = ({
  item,
  idx,
  itemShape,
  hoverIndex,
  getRef,
  handleSwiperItemClick,
  contentWidth,
  primaryButton,
  event,
  data,
  accordionRef,
}: {
  item: ItemType
  idx: number
  itemShape: 'round' | 'square' | undefined
  hoverIndex: number | null
  getRef: (_idx: number, _el: HTMLDivElement) => void
  handleSwiperItemClick: (_idx: number) => void
  contentWidth: number
  primaryButton: string | undefined
  event: any
  data: AccordionCardsType['data']
  accordionRef: React.MutableRefObject<HTMLDivElement[]>
}) => {
  const isExpanded = hoverIndex === idx
  const flexValue = isExpanded ? 8 : 1

  useExposure({ current: accordionRef.current[idx] } as RefObject<HTMLDivElement>, {
    componentType,
    componentName,
    componentTitle: item?.title,
    position: idx + 1,
    componentDescription: item?.subTitle,
  })

  return (
    <div
      key={idx}
      style={{
        flex: `${flexValue} 1 0%`,
        transition: 'all 0.6s',
      }}
      ref={(el: HTMLDivElement | null) => {
        if (el) getRef(idx, el)
      }}
      className={cn('relative cursor-pointer overflow-hidden', itemShape === 'round' ? 'rounded-2xl' : '')}
      onMouseEnter={() => handleSwiperItemClick(idx)}
      onClick={() => {
        handleSwiperItemClick(idx)
      }}
    >
      <a
        href={trackUrlRef(item?.imgLink, `${componentType}_${componentName}`)}
        data-headless-type-name={`${componentType}#${componentName}`}
        data-headless-title-desc-button={`${item?.title}#${item?.subTitle}`}
      >
        <Picture
          source={item?.img?.url}
          className="size-full object-cover [&_img]:h-full [&_img]:object-cover"
          alt={item?.img?.alt || ''}
        />
      </a>
      <div
        style={{
          width: contentWidth,
        }}
        className={cn(
          'absolute inset-x-0 bottom-0 flex w-full items-end justify-between overflow-hidden px-8 pb-8 opacity-0 transition-opacity duration-[600ms] ease-in',
          isExpanded && contentWidth ? 'opacity-100' : 'opacity-0'
        )}
      >
        <div className="mr-16 flex-1 overflow-hidden">
          <Heading as="h3" className="laptop:text-2xl desktop:text-[32px] text-info-primary mb-1 text-xl font-bold">
            {item?.title}
          </Heading>
          <Heading
            as="h4"
            className="lg-desktop:text-[18px] desktop:text-[16px] text-info-primary line-clamp-2 text-[14px] font-[700]"
          >
            {item?.subTitle}
          </Heading>
        </div>
        {primaryButton && (
          <Button
            className={cn('mb-1.5 font-bold')}
            as="a"
            href={trackUrlRef(item?.link, `${componentType}_${componentName}`)}
            data-headless-type-name={`${componentType}#${componentName}`}
            data-headless-title-desc-button={`${item?.title}#${item?.subTitle}#${primaryButton}`}
            onClick={() => event?.primaryButton?.(data, idx)}
          >
            {primaryButton}
            <span className="sr-only">{item?.title ?? item?.subTitle}</span>
          </Button>
        )}
      </div>
    </div>
  )
}

const MobileItem = ({ data, configuration }: { data: ItemType; configuration?: any }) => {
  const ref = useRef<HTMLDivElement>(null)

  useExposure(ref, {
    componentType,
    componentName,
    componentTitle: data?.title,
    position: configuration?.index + 1,
    componentDescription: data?.subTitle,
  })

  return (
    <div
      ref={ref}
      className={cn(
        'bg-container-secondary-1 relative w-full shrink-0 grow-0 overflow-hidden border border-solid',
        configuration?.itemShape === 'round' ? 'rounded-2xl' : ''
      )}
    >
      <a
        href={trackUrlRef(data?.imgLink, `${componentType}_${componentName}`)}
        data-headless-type-name={`${componentType}#${componentName}`}
        data-headless-title-desc-button={`${data?.title}@${data?.subTitle}`}
        data-headless-nav-position={`''#${configuration?.index + 1}`}
      >
        <Picture
          className="size-full object-cover [&_img]:h-full [&_img]:object-cover"
          source={data?.mobileImg?.url || ''}
          alt={data?.mobileImg?.alt || ''}
        />
      </a>
      <div className={cn('absolute inset-x-0 bottom-0 overflow-hidden px-4 pb-4')}>
        <div className="tablet:mb-6 mb-4 flex-1 overflow-hidden">
          <h3
            className="text-info-primary line-clamp-3 text-2xl font-bold leading-[1.2]"
            dangerouslySetInnerHTML={{ __html: data?.title }}
          ></h3>
          <h4
            className="text-info-primary line-clamp-2 text-sm font-bold"
            dangerouslySetInnerHTML={{ __html: data?.subTitle }}
          ></h4>
        </div>

        {configuration?.primaryButton && (
          <Button
            as="a"
            variant="secondary"
            aria-label={data?.title ?? data?.subTitle}
            className={cn('text-info-primary text-sm font-bold')}
            href={trackUrlRef(data.link, `${componentType}_${componentName}`)}
            data-headless-type-name={`${componentType}#${componentName}`}
            data-headless-nav-position={`''#${configuration?.index + 1}`}
            data-headless-title-desc-button={`${data?.title}#${data?.subTitle}#${configuration?.primaryButton}`}
          >
            {configuration?.primaryButton}
          </Button>
        )}
      </div>
    </div>
  )
}

const AccordionCards = React.forwardRef<HTMLDivElement, AccordionCardsType>(
  ({ data, autoplay = { enabled: false, loop: false, interval: 1500 }, className = '', event, ...rest }, ref) => {
    const [hoverIndex, setHoverIndex] = useState<number | null>(0)

    const hoverIndexRef = useRef<number>(0)
    const accordionRef = useRef<HTMLDivElement[]>([])
    const [contentWidth, setContentWidth] = useState<number>(0)

    const innerRef = useRef<HTMLDivElement>(null)
    useImperativeHandle(ref, () => innerRef.current as HTMLDivElement)

    const getRef = (index: number, el: HTMLDivElement) => {
      if (el) {
        accordionRef.current[index] = el
      }
    }

    useEffect(() => {
      const handleResize = () => {
        if (accordionRef.current[hoverIndexRef.current]) {
          setContentWidth(accordionRef.current[hoverIndexRef.current].offsetWidth)
        }
      }
      handleResize()
      const throttleResize = throttle(handleResize, 300)
      window.addEventListener('resize', throttleResize)
      return () => window.removeEventListener('resize', throttleResize)
    }, [])

    const handleSwiperItemClick = useCallback(
      (idx: number) => {
        if (hoverIndex === idx) return
        // 更新宽度相关状态
        setHoverIndex(idx)
        hoverIndexRef.current = idx
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [data?.products, hoverIndex]
    )

    useEffect(() => {
      let autoPlayTimer: NodeJS.Timeout
      if (autoplay?.enabled) {
        autoPlayTimer = setInterval(() => {
          setHoverIndex(prev => {
            const currentIndex = prev ?? 0
            let nextIndex: number

            if (autoplay.loop !== false) {
              // 默认循环播放
              nextIndex = (currentIndex + 1) % data?.products?.length
            } else {
              // 不循环：播放到最后一个后停止
              nextIndex = currentIndex + 1
              if (nextIndex >= data?.products?.length) {
                nextIndex = data?.products?.length - 1
                // 停止自动播放
                if (autoPlayTimer) {
                  clearInterval(autoPlayTimer)
                }
                return currentIndex // 保持在最后一个
              }
            }

            hoverIndexRef.current = nextIndex
            return nextIndex
          })
        }, autoplay?.interval)
      }
      return () => {
        if (autoPlayTimer) {
          clearInterval(autoPlayTimer)
        }
      }
    }, [autoplay?.enabled, autoplay?.interval, autoplay?.loop, data?.products?.length, handleSwiperItemClick])

    return (
      <div {...rest} ref={innerRef}>
        {data?.title && <Title data={{ title: data?.title }} />}
        <div className={cn('laptop:block hidden', { 'aiui-dark': data?.theme === 'dark' })}>
          <div
            className={cn(
              'lg-desktop:h-[calc(560_/_1920*100vw)] desktop:h-[calc(448_/_1440*100vw)] laptop:h-[calc(336_/_1025*100vw)] flex max-h-[560px] min-h-[336px] w-full gap-4 overflow-hidden',
              className
            )}
          >
            {data?.products?.map((item, idx) => (
              <AccordionCardsPcItem
                key={idx}
                item={item}
                idx={idx}
                itemShape={data?.itemShape}
                hoverIndex={hoverIndex}
                getRef={getRef}
                handleSwiperItemClick={handleSwiperItemClick}
                contentWidth={contentWidth}
                primaryButton={data?.primaryButton}
                event={event}
                data={data}
                accordionRef={accordionRef}
              />
            ))}
          </div>
        </div>
        <div className={cn('laptop:hidden block', { 'aiui-dark': data?.theme === 'dark' })}>
          <SwiperBox
            className={cn('h-[400px] !overflow-visible', className)}
            id={'AccordionCards' + data?.key}
            data={{
              list: data?.products,
              configuration: {
                shape: data?.shape,
                itemShape: data?.itemShape,
                primaryButton: data?.primaryButton,
                event: event,
                title: data?.title,
              },
            }}
            Slide={MobileItem}
            breakpoints={{
              0: {
                spaceBetween: 12,
                freeMode: false,
                slidesPerView: 1,
              },
              374: {
                spaceBetween: 12,
                freeMode: false,
                slidesPerView: 1.2,
              },
              768: {
                spaceBetween: 16,
                freeMode: false,
                slidesPerView: 2.3,
              },
            }}
          />
        </div>
      </div>
    )
  }
)

AccordionCards.displayName = 'AccordionCards'
export default withLayout(AccordionCards)
